import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAppleHealthApiKey } from '../services/api';

interface SetupStep {
  title: string;
  description: string;
  completed: boolean;
}

const DeviceSetupWizard: React.FC = () => {
  const { deviceId } = useParams<{ deviceId: string }>();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(0);
  const [apiKey, setApiKey] = useState('');
  const [deviceInfo, setDeviceInfo] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [computerIP, setComputerIP] = useState('');
  const [syncFrequency, setSyncFrequency] = useState('hourly');
  const [metricsToSync, setMetricsToSync] = useState<string[]>(['heart_rate', 'spo2']);

  const steps: SetupStep[] = [
    {
      title: 'Welcome',
      description: 'Let\'s connect your Apple Watch to Edge-on-Age',
      completed: currentStep > 0
    },
    {
      title: 'Configure Settings',
      description: 'Choose what data to sync and how often',
      completed: currentStep > 1
    },
    {
      title: 'Generate API Key',
      description: 'Create secure connection credentials',
      completed: currentStep > 2
    },
    {
      title: 'Install Shortcut',
      description: 'Add the sync shortcut to your iPhone',
      completed: currentStep > 3
    },
    {
      title: 'Test & Complete',
      description: 'Verify everything works',
      completed: currentStep > 4
    }
  ];

  useEffect(() => {
    loadDeviceInfo();
    detectComputerIP();
  }, [deviceId]);

  const loadDeviceInfo = async () => {
    try {
      // Use the public enrollment endpoint instead of authenticated devices endpoint
      const response = await getAppleHealthApiKey(deviceId!);
      setDeviceInfo({
        id: response.deviceId,
        patient_name: response.patientName,
        device_type: response.deviceType
      });
      // Also set the API key if it's already generated
      if (response.apiKey) {
        setApiKey(response.apiKey);
      }
    } catch (err) {
      setError('Failed to load device information');
    } finally {
      setLoading(false);
    }
  };

  const detectComputerIP = async () => {
    // Try to detect from current URL
    const hostname = window.location.hostname;
    if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
      setComputerIP(hostname);
    } else {
      setComputerIP('192.168.1.100'); // Default, user will need to update
    }
  };

  const handleGenerateAPIKey = async () => {
    try {
      setLoading(true);
      const response = await getAppleHealthApiKey(deviceId!);
      setApiKey(response.apiKey);
      setCurrentStep(3);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to retrieve API key');
    } finally {
      setLoading(false);
    }
  };

  const getShortcutDownloadURL = () => {
    const apiURL = process.env.REACT_APP_API_URL || 'http://localhost:3003';
    return `${apiURL}/api/enrollment/shortcut/${deviceId}`;
  };

  const getInstructionsDownloadURL = () => {
    const apiURL = process.env.REACT_APP_API_URL || 'http://localhost:3003';
    return `${apiURL}/api/enrollment/instructions/${deviceId}`;
  };

  const downloadShortcut = () => {
    window.open(getShortcutDownloadURL(), '_blank');
  };

  const downloadInstructions = () => {
    window.open(getInstructionsDownloadURL(), '_blank');
  };

  const generateQRCodeURL = () => {
    // Use backend endpoint to generate QR code
    const apiURL = process.env.REACT_APP_API_URL || 'http://localhost:3003';
    return `${apiURL}/api/enrollment/qr-code/${deviceId}`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="text-center py-8">
            <div className="text-6xl mb-4">⌚</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Welcome to Apple Watch Setup
            </h2>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              This wizard will help you connect your Apple Watch to Edge-on-Age in just a few minutes.
              No technical knowledge required!
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md mx-auto mb-6">
              <h3 className="font-semibold text-blue-900 mb-2">What you'll need:</h3>
              <ul className="text-left text-sm text-blue-800 space-y-1">
                <li>✓ iPhone with iOS 14 or later</li>
                <li>✓ Apple Watch paired with your iPhone</li>
                <li>✓ 5 minutes of time</li>
                <li>✓ Your iPhone and computer on the same WiFi</li>
              </ul>
            </div>
            <button
              onClick={() => setCurrentStep(1)}
              className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-lg font-semibold"
            >
              Get Started →
            </button>
          </div>
        );

      case 1:
        return (
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Configure Your Sync Settings</h2>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Computer IP Address
                </label>
                <p className="text-sm text-gray-600 mb-2">
                  Your iPhone needs to reach your computer. Usually detected automatically.
                </p>
                <input
                  type="text"
                  value={computerIP}
                  onChange={(e) => setComputerIP(e.target.value)}
                  placeholder="192.168.1.100"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Find on Mac: Terminal → <code className="bg-gray-100 px-1">ipconfig getifaddr en0</code>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sync Frequency
                </label>
                <select
                  value={syncFrequency}
                  onChange={(e) => setSyncFrequency(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="15min">Every 15 minutes (High frequency)</option>
                  <option value="30min">Every 30 minutes</option>
                  <option value="hourly">Every hour (Recommended)</option>
                  <option value="4hours">Every 4 hours</option>
                  <option value="daily">Once daily</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Health Metrics to Sync
                </label>
                <div className="space-y-2">
                  {[
                    { id: 'heart_rate', label: 'Heart Rate', recommended: true },
                    { id: 'spo2', label: 'Blood Oxygen (SpO2)', recommended: true },
                    { id: 'respiratory_rate', label: 'Respiratory Rate', recommended: false },
                    { id: 'temperature', label: 'Body Temperature', recommended: false },
                    { id: 'sleep', label: 'Sleep Analysis', recommended: true },
                    { id: 'activity', label: 'Activity & Workouts', recommended: true }
                  ].map((metric) => (
                    <label key={metric.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={metricsToSync.includes(metric.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setMetricsToSync([...metricsToSync, metric.id]);
                          } else {
                            setMetricsToSync(metricsToSync.filter(m => m !== metric.id));
                          }
                        }}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className="flex-1 text-sm font-medium text-gray-900">
                        {metric.label}
                        {metric.recommended && (
                          <span className="ml-2 text-xs text-green-600">(Recommended)</span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex justify-between pt-4">
                <button
                  onClick={() => setCurrentStep(0)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  ← Back
                </button>
                <button
                  onClick={() => setCurrentStep(2)}
                  className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  Continue →
                </button>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Generate Your API Key</h2>
            <p className="text-gray-600 mb-8">
              We'll create a secure key that allows your iPhone to send data to Edge-on-Age.
            </p>

            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <h3 className="font-semibold text-gray-900 mb-3">Your Settings:</h3>
              <div className="text-left space-y-2 text-sm text-gray-700">
                <p>📡 Backend API: <code className="bg-white px-2 py-1 rounded">http://{computerIP}:3003</code></p>
                <p>⏰ Sync Frequency: <strong>{syncFrequency}</strong></p>
                <p>📊 Metrics: <strong>{metricsToSync.length} selected</strong></p>
              </div>
            </div>

            <button
              onClick={handleGenerateAPIKey}
              disabled={loading}
              className="px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 text-lg font-semibold disabled:opacity-50"
            >
              {loading ? 'Generating...' : '🔑 Generate API Key'}
            </button>

            <div className="mt-6">
              <button
                onClick={() => setCurrentStep(1)}
                className="text-gray-600 hover:text-gray-800"
              >
                ← Back to Settings
              </button>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">Install Your Sync Shortcut</h2>
            <p className="text-gray-600 mb-8 text-center">
              Choose your preferred installation method:
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {/* Method 1: QR Code */}
              <div className="bg-white border-2 border-primary-500 rounded-lg p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">📱 Method 1: QR Code (Easiest)</h3>
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  {apiKey ? (
                    <img
                      src={generateQRCodeURL()}
                      alt="Setup QR Code"
                      className="mx-auto"
                    />
                  ) : (
                    <div className="text-center text-gray-500 py-8">
                      Loading QR code...
                    </div>
                  )}
                </div>
                <ol className="text-sm text-gray-700 space-y-2 mb-4">
                  <li>1. Open Camera app on your iPhone</li>
                  <li>2. Point at QR code above</li>
                  <li>3. Tap the notification that appears</li>
                  <li>4. Tap "Add Shortcut" when prompted</li>
                  <li>5. Done! The shortcut is ready to use</li>
                </ol>
                <div className="bg-green-50 border border-green-200 rounded p-3 text-xs text-green-800">
                  ✓ Recommended for most users
                </div>
              </div>

              {/* Method 2: Download Shortcut */}
              <div className="bg-white border-2 border-gray-300 rounded-lg p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">💾 Method 2: Download Shortcut</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Download the pre-configured shortcut file to your iPhone:
                </p>

                <div className="space-y-3">
                  <button
                    onClick={downloadShortcut}
                    className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold flex items-center justify-center space-x-2"
                  >
                    <span>📥</span>
                    <span>Download iOS Shortcut</span>
                  </button>

                  <button
                    onClick={downloadInstructions}
                    className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium flex items-center justify-center space-x-2"
                  >
                    <span>📖</span>
                    <span>Download Setup Instructions</span>
                  </button>

                  <div className="bg-gray-50 rounded p-3 text-xs text-gray-700">
                    <p className="font-medium mb-1">After downloading:</p>
                    <ol className="list-decimal list-inside space-y-1">
                      <li>Open the .shortcut file on your iPhone</li>
                      <li>Tap "Add Shortcut"</li>
                      <li>Grant Health permissions</li>
                      <li>Run the shortcut to sync data</li>
                    </ol>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs font-medium text-gray-600 mb-2">Manual Setup Details:</p>
                  <div className="space-y-2">
                    <div>
                      <label className="text-xs text-gray-500">API Key:</label>
                      <div className="flex items-center space-x-2 mt-1">
                        <code className="flex-1 bg-gray-100 px-2 py-1 rounded text-xs break-all font-mono">
                          {apiKey}
                        </code>
                        <button
                          onClick={() => copyToClipboard(apiKey)}
                          className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                        >
                          Copy
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="text-xs text-gray-500">Backend API URL:</label>
                      <div className="flex items-center space-x-2 mt-1">
                        <code className="flex-1 bg-gray-100 px-2 py-1 rounded text-xs font-mono">
                          http://{computerIP}:3003
                        </code>
                        <button
                          onClick={() => copyToClipboard(`http://${computerIP}:3003`)}
                          className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                        >
                          Copy
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h4 className="font-semibold text-blue-900 mb-2">📋 What happens next:</h4>
              <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                <li>Scan QR code with your iPhone Camera</li>
                <li>Tap "Add Shortcut" (one-tap installation via iCloud)</li>
                <li>Grant Health permissions when you first run the shortcut</li>
                <li>Your Apple Watch data syncs automatically to Edge-on-Age</li>
              </ol>
            </div>

            <div className="flex justify-center space-x-4">
              <button
                onClick={() => setCurrentStep(4)}
                className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-semibold"
              >
                I've Installed the Shortcut →
              </button>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="max-w-2xl mx-auto text-center">
            <div className="text-6xl mb-4">🎉</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Setup Complete!
            </h2>
            <p className="text-gray-600 mb-8">
              Your Apple Watch is now connected to Edge-on-Age.
            </p>

            <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
              <h3 className="font-semibold text-green-900 mb-4">✓ What's Working:</h3>
              <div className="grid md:grid-cols-2 gap-4 text-left">
                <div className="bg-white rounded p-3">
                  <div className="font-semibold text-gray-900 mb-1">🔐 Secure Connection</div>
                  <p className="text-sm text-gray-600">API key authenticated</p>
                </div>
                <div className="bg-white rounded p-3">
                  <div className="font-semibold text-gray-900 mb-1">📊 {metricsToSync.length} Metrics</div>
                  <p className="text-sm text-gray-600">Ready to sync</p>
                </div>
                <div className="bg-white rounded p-3">
                  <div className="font-semibold text-gray-900 mb-1">⏰ {syncFrequency}</div>
                  <p className="text-sm text-gray-600">Automatic sync</p>
                </div>
                <div className="bg-white rounded p-3">
                  <div className="font-semibold text-gray-900 mb-1">⌚ Apple Watch</div>
                  <p className="text-sm text-gray-600">Connected</p>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8 text-left">
              <h3 className="font-semibold text-blue-900 mb-3">📱 Testing Your Setup:</h3>
              <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
                <li>Open Shortcuts app on your iPhone</li>
                <li>Find and tap "Sync to Edge-on-Age"</li>
                <li>Grant Health permissions if prompted</li>
                <li>Should see "✓ Synced" notification</li>
                <li>Check your patient dashboard for new data</li>
              </ol>
            </div>

            <div className="space-y-4">
              <button
                onClick={() => navigate(`/patients/${deviceInfo?.patient_id}`)}
                className="w-full px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-semibold"
              >
                View My Dashboard →
              </button>

              <button
                onClick={() => navigate('/devices')}
                className="w-full px-6 py-2 text-primary-600 hover:text-primary-800"
              >
                Back to Devices
              </button>
            </div>

            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-600 mb-2">Need help?</p>
              <a href="/docs/APPLE_WATCH_QUICKSTART.md" className="text-sm text-blue-600 hover:text-blue-800">
                📖 View troubleshooting guide
              </a>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading && !apiKey) {
    return (
      <div className="flex-1 bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-lg text-gray-600">Loading setup wizard...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={index} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                    index === currentStep
                      ? 'bg-primary-600 text-white'
                      : step.completed
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-300 text-gray-600'
                  }`}>
                    {step.completed ? '✓' : index + 1}
                  </div>
                  <div className="mt-2 text-center">
                    <div className={`text-xs font-medium ${
                      index === currentStep ? 'text-primary-600' : 'text-gray-600'
                    }`}>
                      {step.title}
                    </div>
                  </div>
                </div>
                {index < steps.length - 1 && (
                  <div className={`h-0.5 w-12 mx-2 ${
                    step.completed ? 'bg-green-600' : 'bg-gray-300'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800">{error}</p>
            </div>
          )}

          {renderStepContent()}
        </div>
      </div>
    </div>
  );
};

export default DeviceSetupWizard;
